'use client';

import type { CSSProperties, ReactNode } from 'react';
import { Loader } from '@/components/ui/loader';
import { useReserveDockedArtifactPanel } from '../use-thread-panel-layout-hint';
import { DOCKED_CHAT_PANE_MIN_WIDTH } from './docked-chat-pane-width';

const SPLIT_STYLE: CSSProperties & { '--docked-chat-width': string } = {
  '--docked-chat-width': `${DOCKED_CHAT_PANE_MIN_WIDTH}px`,
};

/**
 * Wraps a thread loading skeleton. When the thread last restored with its
 * artifact panel docked, renders the same split the loaded thread uses (chat
 * at the docked width, panel filling the rest) so opening the real panel does
 * not shrink the chat. The panel column is an inert placeholder.
 *
 * The split is always in the markup and CSS chooses it from the <html> mark
 * set by the head boot script (thread-panel-reserve-boot), so server HTML and
 * the seconds before hydration already show the right layout; the chat no
 * longer paints full width and snaps narrow once JS runs.
 */
export function DockedArtifactPanelReservation({
  children,
}: {
  children: ReactNode;
}) {
  const reserve = useReserveDockedArtifactPanel();
  return (
    <DockedArtifactPanelSplit reserved={reserve}>
      {children}
    </DockedArtifactPanelSplit>
  );
}

/**
 * The split alone, with no hooks: shells that render before the workspace
 * providers (the admission shell) still get the reserved column from the
 * <html> mark.
 */
export function DockedArtifactPanelSplit({
  children,
  reserved = false,
}: {
  children: ReactNode;
  /** Known on the client once the hint is read; CSS decides before that. */
  reserved?: boolean;
}) {
  return (
    <div
      data-thread-loading-panel-reserved={reserved ? 'true' : undefined}
      className="flex h-full min-h-0 w-full overflow-hidden"
      style={SPLIT_STYLE}
    >
      {/* Capped so a container narrower than the docked width never clips. */}
      <div className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden [html[data-thread-panel-reserved]_&]:w-(--docked-chat-width) [html[data-thread-panel-reserved]_&]:max-w-full [html[data-thread-panel-reserved]_&]:flex-none">
        {children}
      </div>
      <div
        aria-hidden="true"
        className="bg-background relative hidden min-w-0 flex-1 [html[data-thread-panel-reserved]_&]:block"
      >
        <span className="bg-border/60 absolute inset-y-0 left-0 w-px" />
        <div className="flex h-full items-center justify-center">
          <Loader size="md" />
        </div>
      </div>
    </div>
  );
}
